// Phase 112 — enemy IFF bracket. When local player's camera roughly faces an enemy,
// project that enemy's world position to screen-space and render a name+distance+health bracket.
import { useEffect, useState, type CSSProperties } from 'react';

type Vec3Like = { x?: number; y?: number; z?: number };

type IFFPlayer = {
  id?: string;
  displayName?: string;
  team?: string | null;
  position?: Vec3Like;
  health?: number;
};

type EnemyIFFBracketProps = {
  localPlayer: IFFPlayer & { yaw?: number; pitch?: number };
  remotePlayers: Record<string, IFFPlayer>;
};

type ProjectedTarget = {
  id: string;
  name: string;
  distance: number;
  health: number;
  screenX: number;
  screenY: number;
};

function dot(ax: number, ay: number, az: number, bx: number, by: number, bz: number) {
  return ax * bx + ay * by + az * bz;
}

export function EnemyIFFBracket({ localPlayer, remotePlayers }: EnemyIFFBracketProps) {
  const [target, setTarget] = useState<ProjectedTarget | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const localTeam = (localPlayer.team ?? '').toLowerCase();
    const lpx = localPlayer.position?.x ?? 0;
    const lpy = (localPlayer.position?.y ?? 0) + 1.6; // eye height
    const lpz = localPlayer.position?.z ?? 0;
    const yaw = localPlayer.yaw ?? 0;
    const pitch = localPlayer.pitch ?? 0;
    // Forward unit vector based on yaw/pitch — matches r3f camera convention used in this project
    const cosP = Math.cos(pitch);
    const fx = Math.sin(yaw) * cosP;
    const fy = Math.sin(pitch);
    const fz = -Math.cos(yaw) * cosP;

    let best: ProjectedTarget | null = null;
    let bestScore = 0;
    const fovHalf = Math.cos((Math.PI / 180) * 6); // ~12-deg cone

    for (const p of Object.values(remotePlayers)) {
      if (!p.position || p.position.x == null || p.position.z == null) continue;
      const team = (p.team ?? '').toLowerCase();
      if (!team || team === localTeam) continue;
      const dx = (p.position.x ?? 0) - lpx;
      const dy = (p.position.y ?? 0) + 0.8 - lpy;
      const dz = (p.position.z ?? 0) - lpz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < 1 || dist > 300) continue;
      const inv = 1 / dist;
      const align = dot(fx, fy, fz, dx * inv, dy * inv, dz * inv);
      if (align < fovHalf) continue;
      if (align > bestScore) {
        bestScore = align;
        // Project: simple perspective ray test — recompute screen offset from forward-relative angle
        // Build right vector perpendicular to forward (assume up = +Y)
        const rx = Math.cos(yaw);
        const rz = Math.sin(yaw);
        // Relative angle horizontal (right) and vertical (up) — approx using projections
        const horiz = dx * rx + dz * rz;
        const vert = dy * Math.cos(pitch) - (Math.sin(pitch) * (dx * Math.sin(yaw) + dz * -Math.cos(yaw)));
        // Approximate camera FOV ~75deg
        const halfFovX = Math.tan((75 * Math.PI / 180) / 2);
        const aspect = viewport.w / viewport.h;
        const halfFovY = halfFovX / aspect;
        const forwardDist = Math.max(0.5, dx * fx + dy * fy + dz * fz);
        const sx = (horiz / forwardDist) / halfFovX;
        const sy = (vert / forwardDist) / halfFovY;
        const screenX = (sx * 0.5 + 0.5) * viewport.w;
        const screenY = (-sy * 0.5 + 0.5) * viewport.h;
        best = {
          id: p.id ?? '',
          name: p.displayName ?? 'Target',
          distance: dist,
          health: Math.max(0, Math.min(100, p.health ?? 100)),
          screenX,
          screenY,
        };
      }
    }
    setTarget(best);
  }, [localPlayer.position?.x, localPlayer.position?.y, localPlayer.position?.z, localPlayer.yaw, localPlayer.pitch, localPlayer.team, remotePlayers, viewport.w, viewport.h]);

  if (!target) return null;

  const bracketColor = '#ff5555';
  const bracketStyle: CSSProperties = {
    position: 'fixed',
    left: target.screenX,
    top: target.screenY,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    zIndex: 60,
    fontFamily: 'monospace',
    color: '#fff',
  };

  const bracketSize = Math.max(24, Math.min(64, 1200 / Math.max(1, target.distance)));

  return (
    <div style={bracketStyle}>
      {/* Corner brackets */}
      <svg width={bracketSize + 30} height={bracketSize + 30} style={{ position: 'absolute', left: -(bracketSize + 30) / 2, top: -(bracketSize + 30) / 2 }}>
        <g stroke={bracketColor} strokeWidth={1.6} fill="none" opacity={0.9}>
          <polyline points={`0,8 0,0 8,0`} />
          <polyline points={`${bracketSize + 22},0 ${bracketSize + 30},0 ${bracketSize + 30},8`} />
          <polyline points={`0,${bracketSize + 22} 0,${bracketSize + 30} 8,${bracketSize + 30}`} />
          <polyline points={`${bracketSize + 22},${bracketSize + 30} ${bracketSize + 30},${bracketSize + 30} ${bracketSize + 30},${bracketSize + 22}`} />
        </g>
      </svg>
      {/* Label above */}
      <div style={{
        position: 'absolute',
        top: -(bracketSize / 2) - 36,
        left: 0,
        transform: 'translateX(-50%)',
        background: 'rgba(10,4,8,0.78)',
        border: `1px solid ${bracketColor}55`,
        padding: '3px 8px',
        fontSize: 11,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}>
        <span style={{ color: bracketColor, fontWeight: 700 }}>{target.name}</span>
        <span style={{ color: 'rgba(255,255,255,0.65)', marginLeft: 6 }}>{Math.round(target.distance)}m</span>
      </div>
      {/* Health bar below */}
      <div style={{
        position: 'absolute',
        top: bracketSize / 2 + 12,
        left: 0,
        transform: 'translateX(-50%)',
        width: 60,
        height: 4,
        background: 'rgba(0,0,0,0.6)',
        border: '1px solid rgba(255,255,255,0.18)',
      }}>
        <div style={{
          width: `${target.health}%`,
          height: '100%',
          background: target.health > 50 ? '#5fdd6a' : target.health > 25 ? '#ffcc66' : '#ff5555',
          transition: 'width 0.15s linear',
        }} />
      </div>
    </div>
  );
}
